'use client';

import { KeyboardEvent, useEffect, useRef, useState } from 'react';
const RESEND_COUNTDOWN_SECONDS = 60;
const OTP_LENGTH = 6;
type UseOtpStepProps = {
  onSubmit: (otp: string) => void;
  onResend: () => void;
  error: string | null;
};
export function useOtpStep({
  onSubmit,
  onResend,
  error
}: UseOtpStepProps) {
  const [otp, setOtp] = useState('');
  const [countdown, setCountdown] = useState(RESEND_COUNTDOWN_SECONDS);
  const inputRef = useRef<HTMLInputElement>(null);

  // Clear the entered code and refocus the first slot when it's rejected.
  useEffect(() => {
    if (!error) {
      return;
    }
    setOtp('');
    inputRef.current?.focus();
  }, [error]);
  useEffect(() => {
    if (countdown <= 0) {
      return;
    }
    const timer = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);
  useEffect(() => {
    function documentPasteHandler(e: ClipboardEvent) {
      const cleaned = pasteTransformer(e.clipboardData?.getData('text') ?? '');
      if (!cleaned) {
        return;
      }
      e.preventDefault();
      setOtp(cleaned);
      // Don't call onSubmit here — setting the value triggers InputOTP's
      // onComplete which calls completeHandler, avoiding a double submission.
    }
    document.addEventListener('paste', documentPasteHandler, {
      capture: true
    });
    return () => {
      document.removeEventListener('paste', documentPasteHandler, {
        capture: true
      });
    };
  }, [onSubmit]);
  function pasteTransformer(pasted: string) {
    return pasted.replace(/\D/g, '').slice(0, OTP_LENGTH);
  }
  function completeHandler(value: string) {
    // input-otp selects the last digit of a full code, and iOS draws its
    // selection handles over the transparent input until it loses focus.
    inputRef.current?.blur();
    onSubmit(value);
  }
  function resendHandler() {
    setOtp('');
    setCountdown(RESEND_COUNTDOWN_SECONDS);
    onResend();
  }
  function keyDownHandler(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key !== 'Enter' || otp.length !== OTP_LENGTH) {
      return;
    }
    completeHandler(otp);
  }
  return {
    otp,
    setOtp,
    countdown,
    completeHandler,
    resendHandler,
    keyDownHandler,
    inputRef
  };
}